// ABOUTME: The breathing round state for 1A: the timeline into a round (storm slide-in, mask, sail fold), a running round, and the cumulative totals sent to the server.
// ABOUTME: The countdown and the swell length derive from the wall clock; state changes once a second, so the frame is not re-rendered more often.

import { useEffect, useRef, useState } from 'react';
import { breathDurAt } from '../../crossing/breathMath';
import { roundProgress } from '../../crossing/breathSky';

export type RoundPhase = 'petals' | 'entering' | 'picker' | 'starting' | 'running' | 'done';
export type RoundIntro = 'rest' | 'sliding' | 'settling' | 'round';
export type SailState = 'set' | 'furled';

// Breathing strategy (TRANS 1): the storm slides in for 2.3 s, then settles under the mask, which arrives at 2.9 s.
const STORM_SETTLE_MS = 2300;
const MASK_IN_MS = 2900;
const INTRO_DONE_MS = 3500;
// A pick (TRANS 3): the mask fades and the sails fold at 0.65 s; the round starts once the fold has finished.
const FOLD_START_MS = 650;
const ROUND_START_AFTER_FOLD_MS = 2500;

export interface BreathTotals {
  breathSecondsTotal: number;
  breathRoundCount: number;
}

interface UseBreathingRoundArgs {
  seedSeconds: number;
  seedRounds: number;
  onRoundComplete: (totals: BreathTotals) => void;
  reducedMotion?: boolean; // every delay is 0 and the sails are furled at once
}

export interface BreathingRound {
  phase: RoundPhase;
  total: number; // seconds in the current round
  leftSec: number; // whole seconds left in the current round, 0 once done
  secondsTotal: number; // cumulative, including completed rounds and seconds folded in from interrupted ones
  roundCount: number; // completed rounds, cumulative
  unsentSeconds: boolean; // a round is running, or seconds were folded in that no save has carried yet
  intro: RoundIntro; // the scene's lead-in: the resting sky, the storm sliding in, settling, then the round's own sky
  sails: SailState;
  sceneX: number; // the sky's progress 0..1: 0 until the round starts, the round's progress while it runs, 1 once done
  weather: boolean; // the rain is up (from the press of Breathing strategy on)
  hasRun: boolean; // a round has been started since the screen opened
  lastRoundDone: boolean; // the last round ran to 0:00
  openStrategy: () => void; // from the petals: slides the storm in, then the mask
  openPicker: () => void; // from a round: the mask at once over the current scene
  start: (secs: number) => void;
  furl: () => void;
  unfurl: () => void;
  totalsNow: () => BreathTotals; // includes the running round's elapsed whole seconds
  elapsedMs: () => number; // milliseconds into the running round, read from the wall clock; 0 outside a round
  swellDur: number; // the ramp's current swell length in seconds (3 outside a round), changing at most once a second
  roundKey: number; // changes with every start, so the cue starts on IN again
}

const TICK_MS = 100;

export function useBreathingRound({ seedSeconds, seedRounds, onRoundComplete, reducedMotion = false }: UseBreathingRoundArgs): BreathingRound {
  const [phase, setPhase] = useState<RoundPhase>('petals');
  const [total, setTotal] = useState(0);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [secondsTotal, setSecondsTotal] = useState(seedSeconds);
  const [roundCount, setRoundCount] = useState(seedRounds);
  const [sentSeconds, setSentSeconds] = useState(seedSeconds);
  const [roundKey, setRoundKey] = useState(0);
  const [intro, setIntro] = useState<RoundIntro>('rest');
  const [sails, setSails] = useState<SailState>('set');
  const [weather, setWeather] = useState(false);
  const [hasRun, setHasRun] = useState(false);
  const [lastRoundDone, setLastRoundDone] = useState(false);
  // The sky the restart's mask shows over: the value the sky had when the last round was left.
  const [frozenX, setFrozenX] = useState(0);

  const startedAtRef = useRef(0);
  const totalRef = useRef(0);
  const completedRef = useRef(false);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  // "Latest ref" pattern: the tick effect is created once per round, so it reads these instead of closing over them.
  const totalsRef = useRef({ secondsTotal, roundCount });
  const onRoundCompleteRef = useRef(onRoundComplete);
  useEffect(() => {
    totalsRef.current = { secondsTotal, roundCount };
    onRoundCompleteRef.current = onRoundComplete;
  });

  const clearTimers = () => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  };
  const after = (ms: number, action: () => void) => {
    timersRef.current.push(setTimeout(action, ms));
  };
  useEffect(() => clearTimers, []);

  useEffect(() => {
    if (phase !== 'running') return;
    const id = setInterval(() => {
      const now = Date.now();
      const deadline = startedAtRef.current + totalRef.current * 1000;
      setElapsedSec(Math.min(totalRef.current, Math.floor((now - startedAtRef.current) / 1000)));
      if (now < deadline || completedRef.current) return;
      completedRef.current = true;
      const totals = {
        breathSecondsTotal: totalsRef.current.secondsTotal + totalRef.current,
        breathRoundCount: totalsRef.current.roundCount + 1,
      };
      setSecondsTotal(totals.breathSecondsTotal);
      setRoundCount(totals.breathRoundCount);
      setSentSeconds(totals.breathSecondsTotal);
      setLastRoundDone(true);
      setPhase('done');
      onRoundCompleteRef.current(totals);
    }, TICK_MS);
    return () => clearInterval(id);
  }, [phase]);

  const sceneX = phase === 'running' ? roundProgress(true, total, Math.max(0, total - elapsedSec))
    : phase === 'done' ? 1
    : phase === 'petals' ? 1
    : hasRun ? frozenX : 0;

  // An interrupted round keeps the whole seconds actually breathed; the round count is for completed rounds only.
  const foldRunningRound = () => {
    if (phase === 'running') setSecondsTotal(seconds => seconds + elapsedSec);
  };

  const openStrategy = () => {
    if (phase !== 'petals') return;
    if (reducedMotion) {
      setWeather(true);
      setIntro('round');
      setPhase('picker');
      return;
    }
    clearTimers();
    setIntro('sliding');
    setWeather(true);
    setPhase('entering');
    after(STORM_SETTLE_MS, () => setIntro('settling'));
    after(MASK_IN_MS, () => setPhase('picker'));
    after(INTRO_DONE_MS, () => setIntro('round'));
  };

  // The restart: the mask over the scene as it stands, with no slide-in.
  const openPicker = () => {
    foldRunningRound();
    clearTimers();
    setFrozenX(sceneX);
    setIntro('round');
    setPhase('picker');
  };

  const begin = (secs: number) => {
    totalRef.current = secs;
    startedAtRef.current = Date.now();
    completedRef.current = false;
    setTotal(secs);
    setElapsedSec(0);
    setRoundKey(key => key + 1);
    setIntro('round');
    setWeather(true);
    setPhase('running');
  };

  const start = (secs: number) => {
    if (phase === 'starting') return;
    foldRunningRound();
    clearTimers();
    setHasRun(true);
    setLastRoundDone(false);
    if (reducedMotion) {
      setSails('furled');
      begin(secs);
      return;
    }
    setPhase('starting');
    if (sails === 'set') {
      after(FOLD_START_MS, () => setSails('furled'));
      after(ROUND_START_AFTER_FOLD_MS, () => begin(secs));
    } else {
      after(FOLD_START_MS, () => begin(secs));
    }
  };

  const elapsedMs = () => {
    if (phase === 'running') return Math.min(Date.now() - startedAtRef.current, totalRef.current * 1000);
    return phase === 'done' ? totalRef.current * 1000 : 0;
  };

  // Whole seconds are enough: the ramp moves slowly, and a change only re-renders the frame once a second.
  const swellDur = phase === 'running' ? breathDurAt(elapsedSec, total) : phase === 'done' ? breathDurAt(total, total) : 3;

  const totalsNow = (): BreathTotals => ({
    breathSecondsTotal: secondsTotal + (phase === 'running' ? elapsedSec : 0),
    breathRoundCount: roundCount,
  });

  return {
    phase,
    total,
    leftSec: phase === 'running' ? Math.max(0, total - elapsedSec) : 0,
    secondsTotal,
    roundCount,
    unsentSeconds: phase === 'running' || secondsTotal > sentSeconds,
    intro,
    sails,
    sceneX,
    weather,
    hasRun,
    lastRoundDone,
    openStrategy,
    openPicker,
    start,
    furl: () => setSails('furled'),
    unfurl: () => setSails('set'),
    totalsNow,
    elapsedMs,
    swellDur,
    roundKey,
  };
}
